import { makeAutoObservable, runInAction } from "mobx";
import {
  adminGasStationsDelete,
  adminGasStationsList,
} from "@pro-sto/api-client";
import type { GasStationResponseDto } from "@pro-sto/api-client";
import { toastStore } from "./ToastStore";
import { parseGasStationField } from "@/pages/GasStationsPage/constants";

export type GasStationsViewMode = "table" | "map";

export interface GasStationDeleteTarget {
  uuid: string;
  name: string;
}

class GasStationsStore {
  stations: GasStationResponseDto[] = [];
  isLoading = false;
  search = "";
  viewMode: GasStationsViewMode = "table";
  selectedUuid: string | null = null;
  deleteTarget: GasStationDeleteTarget | null = null;
  isDeleting = false;

  constructor() {
    makeAutoObservable(this);
  }

  get filteredStations(): GasStationResponseDto[] {
    const query = this.search.trim().toLowerCase();
    if (!query) return this.stations;

    return this.stations.filter((station) => {
      const brand = parseGasStationField(station.brand).toLowerCase();
      const address = (station.address ?? "").toLowerCase();
      return (
        station.name.toLowerCase().includes(query) ||
        brand.includes(query) ||
        address.includes(query) ||
        station.fuels_now.toLowerCase().includes(query)
      );
    });
  }

  loadStations = async () => {
    this.isLoading = true;
    try {
      const response = await adminGasStationsList();
      runInAction(() => {
        this.stations = response ?? [];
        this.isLoading = false;
      });
    } catch {
      runInAction(() => {
        this.isLoading = false;
      });
      toastStore.showError("Не удалось загрузить заправки");
    }
  };

  setSearch = (value: string) => {
    this.search = value;
  };

  setViewMode = (mode: GasStationsViewMode) => {
    this.viewMode = mode;
  };

  selectStation = (uuid: string | null) => {
    this.selectedUuid = uuid;
  };

  requestDelete = (station: GasStationResponseDto) => {
    this.deleteTarget = {
      uuid: station.uuid,
      name: station.name,
    };
  };

  cancelDelete = () => {
    if (this.isDeleting) return;
    this.deleteTarget = null;
  };

  confirmDelete = async () => {
    if (!this.deleteTarget) return;
    this.isDeleting = true;
    const { uuid } = this.deleteTarget;

    try {
      await adminGasStationsDelete({ uuid });
      runInAction(() => {
        this.stations = this.stations.filter((station) => station.uuid !== uuid);
        if (this.selectedUuid === uuid) {
          this.selectedUuid = null;
        }
        this.deleteTarget = null;
        this.isDeleting = false;
      });
      toastStore.showSuccess("Заправка удалена");
    } catch {
      runInAction(() => {
        this.isDeleting = false;
      });
      toastStore.showError("Не удалось удалить заправку");
    }
  };
}

export const gasStationsStore = new GasStationsStore();
export type { GasStationsStore };
